import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableActions } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import { Tenant } from '@interfaces/a-organizar/tenant';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { TenantService } from '@services/http/tenant.service';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable } from 'rxjs';

@Component({
  templateUrl: './tenant-list.component.html',
  styleUrls: ['./tenant-list.component.scss'],
  standalone: false,
})
export class TenantListComponent extends VaultBaseComponent implements OnInit {
  isMobile: boolean = false;
  /** Listagem em andamento: começa verdadeiro para a tabela abrir no esqueleto, não em "Nenhum item". */
  loading = true;
  /** A primeira listagem já chegou: daí em diante o recarregamento usa o loading global. */
  private tenantsLoadedOnce = false;
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  tenants: Tenant[] = [];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 5,
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  search: string = '';
  tenantPermissions = Permissions.Tenant;

  constructor(
    translateService: TranslateService,
    private router: Router,
    private utilsService: UtilsService,
    private tenantService: TenantService,
    private toast: ToastHelperService,
    store: Store<AppState>
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.verifyIsMobile();
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/',
      },
      {
        label: 'Tenants',
        routerLink: '/dashboard/tenant/list',
      },
    ];

    this.getTenants();
  }

  getTenants() {
    // Primeira carga: a tabela mostra o esqueleto, sem o loading global por cima.
    // Recarregamentos (busca, página, exclusão) seguem com o loading global.
    const showGlobalLoading = this.tenantsLoadedOnce;
    if (showGlobalLoading) this.utilsService.setIsLoading(true);
    this.loading = true;
    this.tenantService
      .getTenants(
        {
          ...this.paginationRequest,
          Page: (this.paginationRequest?.Page || 0) + 1,
        },
        this.search
      )
      .subscribe({
        next: response => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.tenantsLoadedOnce = true;
          this.loading = false;
          if (response) {
            this.tenants = response.data;

            const { total, currentPage, pages, itemsPerPage } = response;
            this.pagination = {
              total: total as number,
              currentPage: currentPage as number,
              pages,
              itemsPerPage: itemsPerPage as number,
            };
          }
        },
        error: error => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.tenantsLoadedOnce = true;
          this.loading = false;
          console.error('Error getting tenants: ', error);

          this.toast.showToastError(
            this.t(this.tk.Management.ErrorLoadingTenants),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  handleTablePagination(pagination: PaginatorState) {
    this.paginationRequest = {
      Page: pagination.page,
      PageSize: pagination.rows,
    };
    this.getTenants();
  }

  handleSearch(search: string = ''): void {
    this.search = search;
    this.getTenants();
  }

  handleTableAction(tableActions: TableActions<Tenant>): void {
    switch (tableActions.action) {
      case 'delete':
        this.utilsService.setIsLoading(true);
        tableActions.items.forEach((tenant: Tenant, iUser: number) => {
          this.removeTenant(tenant).subscribe({
            next: () => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastSuccess(this.t(this.tk.Management.TenantDeletedSuccessfully), '');
              if (iUser === tableActions.items.length - 1) {
                this.tenants = [];
                this.getTenants();
              }
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              console.log('removeUser error: ', error);

              this.toast.showToastError(
                this.t(this.tk.Management.ErrorRemovingTenant),
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
            },
          });
        });

        break;
      default:
        break;
    }
  }

  removeTenant(tenant: Tenant): Observable<Tenant | null> {
    return this.tenantService.deleteTenant(tenant.id as string);
  }

  addTenant(): void {
    this.router.navigate(['dashboard/tenant/register']);
  }

  verifyIsMobile() {
    if (this.store) {
      this.store.select(selectIsMobile).subscribe(isMobile => (this.isMobile = isMobile));
    }
  }
}
